// Retain the confirmation handle after bytes have reached storage. A lost
// acknowledgement must retry confirmation, never create a second object.
export function createPresignedUploadCoordinator() {
  const files = new WeakMap();
  return {
    run(file, scope, steps) {
      let scopes = files.get(file);
      if (!scopes) files.set(file, scopes = new Map());
      let task = scopes.get(scope);
      if (!task) scopes.set(scope, task = {});
      if (task.result) return Promise.resolve(task.result);
      if (task.inFlight) return task.inFlight;
      task.inFlight = (async () => {
        if (!task.handle) task.handle = await steps.initiate();
        if (!task.transferred) {
          try { await steps.transfer(task.handle); }
          catch (error) {
            // The object was not acknowledged: a new signed PUT can be issued.
            if (error.status === 403) task.handle = null;
            throw error;
          }
          task.transferred = true;
        }
        const response = await steps.confirm(task.handle);
        task.result = { response, handle: task.handle };
        return task.result;
      })().catch(error => {
        // A regional/traditional fallback must not start a second upload after
        // the first storage PUT succeeded but its confirmation is uncertain.
        if (task.transferred) error.uploadTransferred = true;
        throw error;
      }).finally(() => { task.inFlight = null; });
      return task.inFlight;
    }
  };
}

export function uploadCompletionState(status, assumeProcessing = false) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'failed') return {status: 'error', processingStatus: 'failed', needsProcessing: false};
  const processing = normalized ? normalized !== 'ready' : assumeProcessing;
  return {status: processing ? 'processing' : 'complete', processingStatus: normalized || (processing ? 'queued' : 'ready'), needsProcessing: processing};
}
